import type { MouseEvent } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { toast } from 'react-toastify'
import { routesProfile } from '../../../consts'

/** Возвращает slug или numeric id для сегмента URL /projects/{segment}. */
export function getProjectSlug(
  project: { slug?: string; id?: number } | null | undefined,
  fallbackProjectId?: number | null,
): string {
  if (project?.slug && typeof project.slug === 'string') {
    const s = project.slug.trim()
    if (s) return s
  }
  if (project?.id != null && project.id > 0) return String(project.id)
  if (fallbackProjectId != null && fallbackProjectId > 0) return String(fallbackProjectId)
  return ''
}

export function getActorPathKey(
  actor: { slug?: string | null; id?: number | null; deleted?: boolean } | null | undefined,
  actorUserId?: number | null,
): string {
  if (actor?.slug && typeof actor.slug === 'string') {
    const s = actor.slug.trim()
    if (s) return s
  }
  if (actorUserId != null && actorUserId > 0) return String(actorUserId)
  if (actor?.id != null && actor.id > 0) return String(actor.id)
  return ''
}

export function getTeamSlug(
  team: { slug?: string; id?: number } | null | undefined,
  fallbackTeamId?: number | null,
): string {
  if (team?.slug && typeof team.slug === 'string') {
    const s = team.slug.trim()
    if (s) return s
  }
  if (team?.id != null && team.id > 0) return String(team.id)
  if (fallbackTeamId != null && fallbackTeamId > 0) return String(fallbackTeamId)
  return ''
}

export function getSocialOrderSlug(
  socialOrder: { slug?: string; id?: number } | null | undefined,
  fallbackSocialOrderId?: number | null,
): string {
  if (socialOrder?.slug && typeof socialOrder.slug === 'string') {
    const s = socialOrder.slug.trim()
    if (s) return s
  }
  if (socialOrder?.id != null && socialOrder.id > 0) return String(socialOrder.id)
  if (fallbackSocialOrderId != null && fallbackSocialOrderId > 0) return String(fallbackSocialOrderId)
  return ''
}

export function getGrowthToolSlug(
  growthTool: { slug?: string; id?: number } | null | undefined,
  fallbackGrowthToolId?: number | null,
): string {
  if (growthTool?.slug && typeof growthTool.slug === 'string') {
    const s = growthTool.slug.trim()
    if (s) return s
  }
  if (growthTool?.id != null && growthTool.id > 0) return String(growthTool.id)
  if (fallbackGrowthToolId != null && fallbackGrowthToolId > 0) return String(fallbackGrowthToolId)
  return ''
}

export function createNotificationNavHandlers(navigate: NavigateFunction) {
  const goToProject = (slug: string) => {
    if (!slug) {
      toast.error('Проект недоступен')
      return
    }
    navigate(`/${routesProfile.PROJECTS.path}/${slug}`)
  }

  const goToProjectTeamSpace = (slug: string) => {
    if (!slug) {
      toast.error('Проект недоступен')
      return
    }
    navigate(`/${routesProfile.PROJECTS.path}/${slug}/team-space`)
  }

  const goToUser = (pathKey: string) => {
    if (!pathKey) return
    navigate(`/${routesProfile.USERS.path}/${pathKey}`)
  }

  const onProjectClick = (e: MouseEvent, projectSlug: string) => {
    e.stopPropagation()
    goToProject(projectSlug)
  }

  const onProjectTeamSpaceClick = (e: MouseEvent, projectSlug: string) => {
    e.stopPropagation()
    goToProjectTeamSpace(projectSlug)
  }

  const onUserClick = (e: MouseEvent, pathKey: string) => {
    e.stopPropagation()
    goToUser(pathKey)
  }

  const goToTeam = (slug: string) => {
    if (!slug) {
      toast.error('Команда недоступна')
      return
    }
    navigate(`/${routesProfile.TEAMS.path}/${slug}`)
  }

  const onTeamClick = (e: MouseEvent, teamSlug: string) => {
    e.stopPropagation()
    goToTeam(teamSlug)
  }

  const goToSocialOrder = (slug: string) => {
    if (!slug) {
      toast.error('Социальный заказ недоступен')
      return
    }
    navigate(`/${routesProfile.SOCIALORDERS.path}/${slug}`)
  }

  const onSocialOrderClick = (e: MouseEvent, socialOrderSlug: string) => {
    e.stopPropagation()
    goToSocialOrder(socialOrderSlug)
  }

  const goToGrowthTool = (slug: string) => {
    if (!slug) {
      toast.error('Инструмент роста недоступен')
      return
    }
    navigate(`/${routesProfile.GROWTHTOOLS.path}/${slug}`)
  }

  const onGrowthToolClick = (e: MouseEvent, growthToolSlug: string) => {
    e.stopPropagation()
    goToGrowthTool(growthToolSlug)
  }

  return {
    goToProject,
    goToProjectTeamSpace,
    goToUser,
    goToTeam,
    goToSocialOrder,
    goToGrowthTool,
    onProjectClick,
    onProjectTeamSpaceClick,
    onUserClick,
    onTeamClick,
    onSocialOrderClick,
    onGrowthToolClick,
  }
}

export function actorNameClickProps(
  pathKey: string,
  onUserClick: (e: MouseEvent, pathKey: string) => void,
): { onClick?: (e: MouseEvent) => void; sx: { cursor: string } } {
  if (!pathKey) {
    return { sx: { cursor: 'inherit' } }
  }
  return {
    onClick: (e: MouseEvent) => onUserClick(e, pathKey),
    sx: { cursor: 'pointer' },
  }
}

export function projectNameClickProps(
  pathKey: string,
  onProjectClick?: (e: MouseEvent, pathKey: string) => void,
): { onClick?: (e: MouseEvent) => void; sx: { cursor: string } } {
  if (!pathKey || !onProjectClick) {
    return { sx: { cursor: 'inherit' } }
  }
  return {
    onClick: (e: MouseEvent) => onProjectClick(e, pathKey),
    sx: { cursor: 'pointer' },
  }
}
